import * as B from '@blueprintjs/core';
import React, {SyntheticEvent} from 'react';

import Label from 'app/components/Label';
import Modal from 'app/components/Modal/Modal';
import {api} from 'app/modules/Remote';
import {ApiOrganization} from 'app/modules/Remote/Organization';
import * as C from 'app/utils/constants';
import * as inviteUtils from 'app/utils/inviteUtils';

import cs from './InviteUsersModal.styl';
import {usePushNotification} from '../Notification';

const InviteUsersModal: React.FunctionComponent<
  React.PropsWithChildren<{
    isOpen: boolean;
    onClose: (event: SyntheticEvent<HTMLElement> | undefined) => unknown;
    permissions: inviteUtils.PERMISSIONS_TYPE[];
    organizationId: ApiOrganization['id'];
  }>
> = ({isOpen, onClose, permissions, organizationId}) => {
  const [selectedRoleId, setSelectedRoleId] = React.useState<inviteUtils.RoleId>(
    C.USER_ROLE_READONLY
  );
  const [emailToInvite, setEmailToInvite] = React.useState<string>('');
  const [isInvitingUser, setIsInvitingUser] = React.useState<boolean>(false);

  const pushNotification = usePushNotification();

  const sendInvite = React.useCallback(async () => {
    setIsInvitingUser(true);
    try {
      await api.organizations.invite(organizationId, emailToInvite, selectedRoleId);
      pushNotification({
        message: `Invitation sent to ${emailToInvite}`,
        options: {intent: B.Intent.SUCCESS},
      });
      setEmailToInvite('');
    } catch (error) {
      console.error('Failed to invite user:', error);
      pushNotification({
        message: `Error sending invitation to ${emailToInvite}`,
        options: {intent: B.Intent.DANGER},
      });
    } finally {
      setIsInvitingUser(false);
    }
  }, [organizationId, pushNotification, emailToInvite, selectedRoleId]);

  return (
    <Modal isOpen={isOpen} headerText="Invite User" onClose={onClose} hideActions={true}>
      <div className={cs.container}>
        <div className={cs.instructions}>
          Invite a teammate by selecting their role and entering their email below. Invites expire
          after 14 days.
        </div>

        <div className={cs.form}>
          <div>
            <Label text="Role" />
            <B.HTMLSelect
              value={selectedRoleId}
              className={cs.role}
              onChange={(e) => setSelectedRoleId(e.target.value as inviteUtils.RoleId)}
            >
              {inviteUtils.roleIds.map((roleId) => (
                <option key={roleId} value={roleId}>
                  {inviteUtils.ROLE_NAMES[roleId]}
                </option>
              ))}
            </B.HTMLSelect>
          </div>
          <div>
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                sendInvite();
              }}
            >
              <Label text="Email address" />
              <B.InputGroup
                type="email"
                value={emailToInvite}
                onChange={(event) => setEmailToInvite(event.target.value.trim())}
                disabled={isInvitingUser}
                rightElement={
                  <B.Button
                    text={'Send invite'}
                    disabled={!emailToInvite}
                    type={'submit'}
                    loading={isInvitingUser}
                  />
                }
              />
            </form>
          </div>
        </div>
        <B.Callout className={cs.callout} intent={B.Intent.PRIMARY} icon={null}>
          {inviteUtils.ROLE_SUMMARIES[selectedRoleId]}{' '}
          <a href={inviteUtils.ROLE_SUPPORT_DOC_LINK} target="_blank" rel="noopener noreferrer">
            See support doc here.
          </a>
          <table>
            <tbody>
              {permissions.map((permission) => {
                const permissionDef = inviteUtils.PERMISSIONS.find((p) => p.type === permission);
                if (permissionDef) {
                  const {label, actions} = permissionDef;

                  const listActions = actions.filter(
                    (a) => a.roles.includes(selectedRoleId) && !!a.text
                  );
                  const noteActions = actions.filter(
                    (a) => a.roles.includes(selectedRoleId) && !!a.note
                  );

                  return (
                    <tr key={permission}>
                      <th>{label}</th>
                      <td>
                        {noteActions.length ? (
                          <>
                            {noteActions.map((a, i) => (
                              <em key={i}>{a.note}</em>
                            ))}
                          </>
                        ) : null}
                        {listActions.length ? (
                          <ul>
                            {listActions.map((a, i) => (
                              <li key={i}>{a.text}</li>
                            ))}
                          </ul>
                        ) : null}
                      </td>
                    </tr>
                  );
                } else {
                  return null;
                }
              })}
            </tbody>
          </table>
        </B.Callout>
      </div>
    </Modal>
  );
};

export default InviteUsersModal;
